
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export enum LogLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  DEBUG = 'debug'
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  details?: any;
  user_id?: string;
  academic_year?: string;
}

export interface LogSummary {
  count: number;
  lastLog: Date;
  categories: { [key: string]: number };
  levels: { [key: string]: number };
}

/**
 * Add a log entry to the system
 */
export async function addLogEntry(
  level: LogLevel,
  category: string,
  message: string,
  details?: any,
  user_id?: string,
  academic_year?: string
): Promise<{ success: boolean; error?: string; log_id?: string }> {
  try {
    const { data, error } = await supabase
      .from('system_logs')
      .insert({
        level,
        category,
        message,
        details,
        user_id,
        academic_year
      })
      .select('id')
      .single();

    if (error) throw error;

    // Only log to console in development
    if (import.meta.env.DEV) {
      console.log(`[${level.toUpperCase()}][${category}] ${message}`);
    }

    return { success: true, log_id: data.id };
  } catch (error) {
    console.error('Failed to add log entry:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

/**
 * Convenience logging methods
 */
export const logInfo = (category: string, message: string, details?: any, user_id?: string, academic_year?: string) => 
  addLogEntry(LogLevel.INFO, category, message, details, user_id, academic_year);

export const logWarning = (category: string, message: string, details?: any, user_id?: string, academic_year?: string) => 
  addLogEntry(LogLevel.WARNING, category, message, details, user_id, academic_year);

export const logError = (category: string, message: string, details?: any, user_id?: string, academic_year?: string) => 
  addLogEntry(LogLevel.ERROR, category, message, details, user_id, academic_year);

export const logSuccess = (category: string, message: string, details?: any, user_id?: string, academic_year?: string) => 
  addLogEntry(LogLevel.SUCCESS, category, message, details, user_id, academic_year);

export const logDebug = (category: string, message: string, details?: any, user_id?: string, academic_year?: string) => 
  addLogEntry(LogLevel.DEBUG, category, message, details, user_id, academic_year);

/**
 * Fetch logs with filtering and pagination
 */
export async function fetchLogs({
  level,
  category,
  from_date,
  to_date,
  search,
  academic_year,
  page = 1,
  limit = 25
}: {
  level?: LogLevel;
  category?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  academic_year?: string;
  page?: number;
  limit?: number;
}): Promise<{ logs: LogEntry[]; count: number; error?: string }> {
  try {
    let query = supabase
      .from('system_logs')
      .select('*', { count: 'exact' })
      .order('timestamp', { ascending: false });
    
    // Apply filters
    if (level) {
      query = query.eq('level', level);
    }
    
    if (category) {
      query = query.eq('category', category);
    }
    
    if (academic_year) {
      query = query.eq('academic_year', academic_year);
    }
    
    if (from_date) {
      query = query.gte('timestamp', from_date);
    }
    
    if (to_date) {
      query = query.lte('timestamp', to_date);
    }
    
    if (search) {
      query = query.or(`message.ilike.%${search}%,details.ilike.%${search}%`);
    }
    
    // Apply pagination
    const startIndex = (page - 1) * limit;
    query = query.range(startIndex, startIndex + limit - 1);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return { 
      logs: data as LogEntry[], 
      count: count || 0 
    };
  } catch (error) {
    console.error('Failed to fetch logs:', error);
    return { 
      logs: [], 
      count: 0, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

/**
 * Get summary statistics about logs
 */
export async function getLogsSummary(academic_year?: string): Promise<LogSummary | null> {
  try {
    // Get total count with optional academic year filter
    let query = supabase
      .from('system_logs')
      .select('*', { count: 'exact' });
      
    if (academic_year) {
      query = query.eq('academic_year', academic_year);
    }
    
    const { count, error } = await query;
    
    if (error) throw error;
    
    // Get latest log
    const { data: latestLog, error: latestError } = await supabase
      .from('system_logs')
      .select('timestamp')
      .order('timestamp', { ascending: false })
      .limit(1);
      
    if (latestError) throw latestError;
    
    // Get category counts
    const { data: categoryData, error: categoryError } = await supabase
      .rpc('get_log_category_counts', { year_filter: academic_year || null });
      
    if (categoryError) throw categoryError;
    
    // Get level counts
    const { data: levelData, error: levelError } = await supabase
      .rpc('get_log_level_counts', { year_filter: academic_year || null });
      
    if (levelError) throw levelError;
    
    // Process data into summary format
    const categories: { [key: string]: number } = {};
    categoryData.forEach((item: any) => {
      categories[item.category] = parseInt(item.count);
    });
    
    const levels: { [key: string]: number } = {};
    levelData.forEach((item: any) => {
      levels[item.level] = parseInt(item.count);
    });
    
    return {
      count: count || 0,
      lastLog: latestLog && latestLog.length > 0 ? new Date(latestLog[0].timestamp) : new Date(),
      categories,
      levels
    };
  } catch (error) {
    console.error('Failed to get logs summary:', error);
    toast.error('Failed to fetch logs summary');
    return null;
  }
}
